import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { EmailGet, EmailService, MessageEmail, EnrichedMessage, EmailArchivedEvent } from '@app/services/email.service';
import { GraphMailService, MailFolderSearchItem } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { Item } from '@app/core/components/listbox/listbox.component';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { GridOptions, GridRow } from '@app/core/components/grid/grid.types';
import { Grid } from '@app/core/components/grid/grid.model';
import { IRowNode, RowSelectedEvent } from '@ag-grid-community/core';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { ToastService } from '@app/core/services/toast.service';
import { catchError, concatMap, debounceTime, filter, first, firstValueFrom, from, interval, map, of, Subject, Subscription, switchMap, takeUntil, tap, timer } from 'rxjs';
import { StateService } from '@app/core/services/state.service';
import { MgtLogin } from '@microsoft/mgt';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { RememberService } from '@app/core/services/remember.service';
import { AccountInfo } from '@azure/msal-browser';
import { ModalService } from '@app/core/services/modal.service';
import { Attachment, FileAttachment, ItemAttachment, MailFolder, ReferenceAttachment } from '@microsoft/microsoft-graph-types';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EmailArchiveDialogComponent } from '@app/modal/email-archive-dialog/email-archive-dialog.component';
import { EmailPersonInfoDialogComponent } from '@app/modal/email-person-info-dialog/email-person-info-dialog.component';
import { EmailMoveToFolderComponent } from '@app/modal/email-move-to-folder/email-move-to-folder.component';
import { PopupService } from '@app/core/services/popup.service';
import { EmailPersonSaveResponse } from './email-person-info-form/email-person-info-form.component';

@Component({
    selector: 'tt-email',
    templateUrl: './email.component.html',
    styleUrls: ['./email.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class EmailComponent extends ComponentBaseComponent implements AfterViewInit, OnDestroy {
    expanded = true;

    /**
     * Reference to the mgt-login element.
     */
    @ViewChild('login')
    public login?: ElementRef<MgtLogin>;

    /**
     * The page load for the email page.
     */
    public get?: EmailGet;

    /**
     * List of companies.
     */
    public companies: BusinesscoItem[] = [];

    /**
     * List of enriched emails displayed.
     */
    public email?: EnrichedMessage;

    /**
     * List of available mailboxes connected to the currenly logged in email account.
     */
    public sharedMailboxes: Item[] = [];

    /**
     * List of buttons appended to the email viewer.
     */
    public emailViewButtons = [{ id: 'open', title: 'open_in_new_tab', icon: 'fas fa-external-link-alt', onClick: () => !!this.email && window.open(this.email.webLink, '_blank') }];

    /**
     * Promise resolving when the emails from mgt api is finished retrieving for the first time upon page load.
     */
    private retrievingInitialEmailsPromise: Promise<MessageEmail[]>;

    /**
     * Calls and awaits all prequisite data necessary for retrieving initial messages upon page load. Promise resolve the intial messages.
     */
    private async retrievingInitialEmails(): Promise<MessageEmail[]> {
        await this.loadPageGet();

        this.readLastArchivedGrid();
        setTimeout(() => {
            this.login?.nativeElement.addEventListener('click', this.onLoginInitiated);
            this.login?.nativeElement.addEventListener('loginCompleted', this.onUserLoggedIn);
            this.login?.nativeElement.addEventListener('logoutInitiated', this.onUserLogoutInitiated);
        });

        this.loadSharedMailboxes();

        try {
            await new Promise<void>((resolve) => this.emailService.initialMailFoldersLoaded$.subscribe({ next: () => resolve() }));
            // await this.loadMailFolders();

            if (this.isInvalidSelectedFolder()) await this.setDefaultMailFolder();

            return this.getMessageEmails();
        } catch (error) {
            console.error(error);
            return [];
        }
    }

    /**
     * Promise resolving when the emails have been enriched with touchtime information for the first time upon page load.
     */
    private retrievingInitialEnrichedEmailsPromise: Promise<EnrichedMessage[]>;

    /**
     * Awaits for initial messages to be retrievied before enriching the email with TouchTime data. Promise resolves the enriched messages.
     */
    private async retrievingInitialEnrichedEmails(): Promise<EnrichedMessage[]> {
        const messages = await this.retrievingInitialEmailsPromise;
        return this.getEnrichedMessages(messages);
    }

    /**
     * Observable for fetching and loading new data to the grid.
     */
    private reloadData$ = new Subject<void>();

    /**
     * Observable for when user logs in.
     */
    private userLoggedIn$ = new Subject<void>();

    /**
     * The grid displaying emails.
     */
    public grid: GridOptions = new Grid()
        .setRememberId('w_email_grid')
        .setLoadDataFunction({ method: 2945 })
        .setRemoveRowFunction({ confirm: false })
        .setCustomToolbarButtons([
            { name: 'refreshEmails', tooltip: 'w_email_refresh', func: this.reloadGridData.bind(this), spinOnAction: true, icon: 'far fa-sync', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', translateTooltip: true },
            { name: 'createVouchers', text: 'w_email_create_voucher', func: this.createVouchersFromSelectedRows.bind(this), icon: 'far fa-calculator', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', translate: true, disabled: () => (this.get?.show_buttons_accounting !== '1' ? 'hidden' : (this.grid.gridfunc?.getIsSelectedRows() ?? []).length === 0) },
            { name: 'toggle_mark_as_read', text: 'toggle_mark_as_read', translate: true, func: this.toggleMarkAsReadSelectedRows.bind(this), icon: 'far fa-envelope-open', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', translateTooltip: true },
            { name: 'moveToArchiveFolder', tooltip: 'w_email_move_to', func: this.moveToArchiveFolder.bind(this), icon: 'far fa-folder-upload', cssClass: 'tt-button tt-button--primary im-grid-btn-xs-r', translateTooltip: true, disabled: () => (this.grid.gridfunc?.getIsSelectedRows() ?? []).length === 0 },
            { name: 'deleteSelected', tooltip: 'email_delete_selected', func: this.deleteSelectedRows.bind(this), icon: 'far fa-trash', cssClass: 'tt-button tt-button--danger im-grid-btn-xs-r', translateTooltip: true, disabled: () => (this.grid.gridfunc?.getIsSelectedRows() ?? []).length === 0 },
        ])
        .setOnSelect(this.setEmailFromSelectedRow.bind(this))
        .setKendoConfig({ selectable: 'single' });

    public gridLastArchived?: GridOptions;

    private async toggleMarkAsReadSelectedRows() {
        const selectedRows = this.grid.gridfunc?.getIsSelectedRows() ?? [];

        if (selectedRows.length > 0) {
            let isRead = false;
            const markedAsUnreadCount = selectedRows.filter((row) => !['1', 1, true, 'true'].includes(this.grid.gridfunc?.getRowNode(row)!.data['read_ok'])).length;
            const markedAsReadCount = selectedRows.filter((row) => ['1', 1, true, 'true'].includes(this.grid.gridfunc?.getRowNode(row)!.data['read_ok'])).length;

            if (markedAsReadCount === 0) {
                isRead = true;
            } else if (markedAsUnreadCount === 0) {
                isRead = false;
            } else {
                isRead = markedAsUnreadCount > markedAsReadCount;
            }

            for (let row of selectedRows) {
                const rowNode = this.grid.gridfunc?.getRowNode(row);
                if (!!rowNode) {
                    await this.setEmailIsRead(rowNode, isRead);
                }
            }
        }
    }

    private readLastArchivedGrid() {
        if (!this.get || this.get.gb_email_main !== 'LAST_ARCHIVED') return;

        if (!this.gridLastArchived) {
            this.gridLastArchived = new Grid().setRememberId('w_email.last_archived.grid').setLoadDataFunction({ method: 3871, parameters: () => this.get });
        } else {
            this.gridLastArchived.gridfunc?.read();
        }
    }
    /**
     * Reference to old angularJs constant 'base64', as it encodes emojis which other functions from base64 service in ts does not.
     */
    private base64: any;

    constructor(
        private graphMail: GraphMailService,
        private graphAuth: GraphAuthService,
        private emailService: EmailService,
        private logistic: LogisticService,
        private toast: ToastService,
        private state: StateService,
        private remember: RememberService,
        private modal: ModalService,
        private dialog: MatDialog,
        private popup: PopupService,
    ) {
        super();

        this.base64 = emailService.getBase64();

        this.loadCompanies();

        this.retrievingInitialEmailsPromise = this.retrievingInitialEmails();
        this.retrievingInitialEnrichedEmailsPromise = this.retrievingInitialEnrichedEmails();

        this.subscribeToReloadGridData();

        this.userLoggedIn$.pipe(debounceTime(500)).subscribe({
            next: () =>
                this.getMicrosoftAccount().subscribe({
                    next: this.onUserLoggedInAndAccountRetrieved,
                    error: this.onGetMicrosoftAccountFailed,
                }),
        });
    }

    public toggleFolderPane() {
        if (!this.get) return;

        this.get.folders_is_expanded = this.get.folders_is_expanded === '1' ? '0' : '1';

        this.remember.remember('w_email.folders_is_expanded', this.get.folders_is_expanded);
    }

    private async subscribeToReloadGridData() {
        console.log('subscribeToReloadGridData');
        await this.retrievingInitialEnrichedEmailsPromise;
        console.log('promise completed');
        this.reloadData$.pipe(debounceTime(500), switchMap(this.getEnrichedMessagesForGrid)).subscribe({
            next: (value) => this.grid.gridfunc?.setDataSource(value),
            complete: () => this.grid.gridfunc?.gridProgress(false),
        });
    }

    /**
     * Create observable which retireves messages from mgt and inbetween loads it to the grid while enriching messages.
     * Observable updates with the enriched messages.
     *
     * @returns observable for enriching message, which also load inbetween fetching loads data to grid.
     */
    private getEnrichedMessagesForGrid = () => {
        console.log('getEnrichedMessagesForGrid');
        this.grid.gridfunc?.gridProgress(true);

        return from(this.getMessageEmails()).pipe(
            tap({
                next: (value) => {
                    console.log('message');
                    const messages = [...value.map((message) => Object.assign({}, message))];
                    this.grid.gridfunc?.setDataSource(messages);
                },
            }),
            concatMap((messages) => from(this.getEnrichedMessages(messages))),
        );
    };

    /**
     * Opens the file attachment in a new tab.
     *
     * @param event the attachment to open in a new tab.
     */
    public openFileAttachment(event: { attachment: Attachment | FileAttachment | ItemAttachment | ReferenceAttachment }) {
        if ('contentBytes' in event.attachment && !!event.attachment.contentBytes && !!event.attachment.contentType) {
            const blobData = atob(event.attachment.contentBytes);
            let byteNumbers = new Array(blobData.length);

            for (let i = 0; i < blobData.length; i++) {
                byteNumbers[i] = blobData.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const fileBlob = new Blob([byteArray], { type: event.attachment.contentType });
            const blobUrl = URL.createObjectURL(fileBlob);
            window.open(blobUrl, '_blank');
        }
    }

    /**
     * Updates view when an email has been archived.
     *
     * @param event the email archive event with response codes and the archived email.
     */
    public async onEmailArchived(event: EmailArchivedEvent) {
        if (!!this.get && event.errorcode === '0') {
            let messageMoved = false;
            const emailKeyno = event.email_keyno_new;

            if (this.canMoveToArchiveFolder()) {
                try {
                    await this.graphMail.moveMessageToFolder(event.email.id, this.get.auto_archieve_folder_id, this.get.current_mailbox);

                    await this.removeRowAndSelectNext(event.email);
                    messageMoved = true;
                } catch (error) {
                    console.error(error);
                    this.toast.addToast({ id: 'w_email.autoarchivefailed', title: 'could_not_move_to_archive_folder', translateTitle: true, message: error instanceof Error ? error.message : ' ', translateMessage: false, type: 'danger' });
                }
            }

            if (!messageMoved) {
                await this.updateEmailAndRow(event.email);
            }

            if (Number(event.order_internal_no) > 0) {
                this.popup.openPopup(`orderhead/${event.order_internal_no}`, { rememberId: 'w_email_orderhead_popup' });
            } else if (event.goto_reply === '1') {
                if (!!this.email) {
                    this.email.email_keyno = +event.email_keyno_new;
                }

                if (event.reply_all === '1') {
                    this.replyAllEmail(emailKeyno);
                } else {
                    this.replyEmail(emailKeyno);
                }
                // this.popup.openPopup(`emailcreate/emailview/email_keyno/${event.email_keyno_new}`);

                // this.state.go('emailcreate', { webpage: 'emailview', argtype: 'email_keyno', argvalue: event.email_keyno_new });
            }
        }
    }

    /**
     * Sets the selected email to the email represented in the given row selection event.
     *
     * @param $event the row selection event to set selected email from.
     */
    private async setEmailFromSelectedRow({ $event, $item }: { $event: RowSelectedEvent<any, any>; $item?: GridRow | undefined }) {
        delete this.email;

        if (!!$event && $event.node.isSelected() && !!$event.node.data) {
            const email = $event.node.data as EnrichedMessage;

            if (!email.read_ok) {
                this.setEmailIsRead($event.node);
            }

            const attachments = await this.getEmailAttachments(email);
            email.attachments = attachments.filter((attachment) => !attachment.isInline);

            email.email_body_html = this.base64.urlencode(this.replaceInlineAttachmentsWithContentBytes(this.base64.urldecode(email.email_body_html), attachments));

            this.email = email;
        }
    }

    /**
     * Retrieves emails attachment from the email with id given.
     *
     * @param email the email with id (id on the email from mgt api) to fetch attachments for.
     * @returns list of attachments for the email with id given.
     */
    private async getEmailAttachments(email: { id: string }): Promise<(Attachment | FileAttachment | ItemAttachment | ReferenceAttachment)[]> {
        if (!!this.get && !!email.id) {
            return (await this.graphMail.getAttachmentsFromMessageId(email.id, this.get.current_mailbox))?.value ?? [];
        }

        throw Error('Page must be loaded and id of the email to retrieve attachments must be defined');
    }

    /**
     * Replaces the placeholder content id of the given html content with the contentbytes of the given attachments where content id matches.
     *
     *
     * @param htmlContent the html content to replace placeholder inline attachments for.
     * @param attachments the attachment list to use for replacing the inline attachments.
     * @param sizeLimit whether to not replace inline attachment where the attachment size exceeds 10 mb.
     * @returns the html content with the placeholder attachments replaced.
     */
    private replaceInlineAttachmentsWithContentBytes(htmlContent: string, attachments: (Attachment | FileAttachment | ItemAttachment | ReferenceAttachment)[], sizeLimit = true) {
        let html = htmlContent;

        attachments.forEach((attachment) => {
            if (attachment.isInline === true && 'contentId' in attachment) {
                if (!sizeLimit || (attachment.size ?? 0) < 10000000) {
                    html = html.replaceAll(`cid:${attachment.contentId}`, `data: ${attachment.contentType}; base64, ${attachment.contentBytes} `);
                } else {
                    html = html.replaceAll(`cid:${attachment.contentId}`, `cid:${attachment.contentId}" alt="Image size too big`);
                }
            }
        });

        return html;
    }

    /**
     * Marks the email represented in the given row node as read in outlook and in grid.
     *
     * @param rowNode the row node which represents the email to be marked as read.
     */
    private async setEmailIsRead(rowNode: IRowNode, isRead = true) {
        try {
            await this.graphMail.updateMessage(rowNode.data.id, { isRead: isRead }, this.get!.current_mailbox);

            if (isRead) {
                rowNode.setData({ ...rowNode.data, read_ok: true, item_glyphicon: rowNode.data.item_glyphicon.replace('fas-envelope ', 'fas-envelope-open '), item_glyphicon_color: rowNode.data.item_glyphicon_color.replaceAll('red', 'black') });
            } else {
                rowNode.setData({ ...rowNode.data, read_ok: false, item_glyphicon: rowNode.data.item_glyphicon.replace('fas-envelope-open ', 'fas-envelope '), item_glyphicon_color: rowNode.data.item_glyphicon_color.replace('black', 'red').replace('black', 'red') });
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Enriches the given email anew and updates the selected email and the grid row the email is represented by with the new enriched values.
     *
     * @param email the email to update.
     */
    private async updateEmailAndRow(email: EnrichedMessage) {
        if (!!this.get) {
            const rowNode = this.grid.gridfunc?.getRowNode(email);

            if (!!rowNode) {
                const updatedEnrichedEmail = (await this.emailService.enrichEmails({ businessco_no: this.get.businessco_no, current_account_email: this.get.current_account_email, emails: [email] }))[0];
                this.email = { ...email, ...updatedEnrichedEmail };
                rowNode.setData(this.email);
            }
        }
    }

    /**
     * Enriches the given row which respresents a email anew and updates the row in the grid with the new enriched values.
     *
     * @param row the grid row representing the email to be enriched and updates in grid.
     */
    private async updateEmailRow(row: GridRow) {
        if (!!this.get) {
            const rowNode = this.grid.gridfunc?.getRowNode(row);

            if (!!rowNode) {
                const updatedEnrichedEmail = (await this.emailService.enrichEmails({ businessco_no: this.get.businessco_no, current_account_email: this.get.current_account_email, emails: [{ ...row, read_ok: rowNode.data.read_ok } as MessageEmail] }))[0];
                rowNode.setData({ ...row, ...updatedEnrichedEmail });
            }
        }
    }

    /**
     * Moves the currently selected email to the archive folder, if archiving is possible.
     */
    public async moveSelectedEmailToArchiveFolder() {
        if (!!this.get && !!this.email && this.canMoveToArchiveFolder()) {
            try {
                await this.graphMail.moveMessageToFolder(this.email.id, this.get.auto_archieve_folder_id, this.get.current_mailbox);
                this.removeRowAndSelectNext(this.email);
            } catch (error) {
                console.error(error);
                this.toast.addToast({ id: 'w_email.autoarchivefailed', title: 'could_not_move_to_archive_folder', translateTitle: true, message: error instanceof Error ? error.message : ' ', translateMessage: false, type: 'danger' });
            }
        }
    }

    public async moveToArchiveFolder() {
        if (!!this.get) {
            const selectedRows: EnrichedMessage[] = (this.grid.gridfunc?.getIsSelectedRows() ?? []) as EnrichedMessage[];

            const dialogRef = this.dialog.open(EmailMoveToFolderComponent, { data: { get: this.get }, width: '60rem' });

            const folderId = await firstValueFrom(dialogRef.beforeClosed());

            if (!folderId) return;

            if (selectedRows.length > 0) {
                try {
                    this.grid.gridfunc?.gridProgress(true);
                    delete this.email;

                    for (let row of selectedRows as EnrichedMessage[]) {
                        await this.graphMail.moveMessageToFolder(row.id, folderId, this.get.current_mailbox);
                        this.grid.gridfunc?.removeRow(row);
                    }

                    // await Promise.all(selectedRows.map((row) => this.updateEmailRow(row)));
                } finally {
                    this.grid.gridfunc?.gridProgress(false);
                }
            }
            // try {
            //     await this.graphMail.moveMessageToFolder(this.email.id, this.get.auto_archieve_folder_id);
            //     this.removeRowAndSelectNext(this.email);
            // } catch (error) {
            //     console.error(error);
            //     this.toast.addToast({ id: 'w_email.autoarchivefailed', title: 'could_not_move_to_archive_folder', translateTitle: true, message: error instanceof Error ? error.message : ' ', type: 'danger' });
            // }
        }
    }

    public async openEmailPersonInfoForm() {
        const dialogRef: MatDialogRef<EmailPersonInfoDialogComponent, EmailPersonSaveResponse | undefined> = this.dialog.open(EmailPersonInfoDialogComponent, { data: { get: this.get, email: this.email }, width: 'min(60rem, 95vw)' });

        const event = await firstValueFrom(dialogRef.beforeClosed());

        if (!!event && !!this.email) {
            this.email.pers_no = '' + event.pers_no;
            this.updateEmailAndRow(this.email);
        }
        // const event = await firstValueFrom(dialogRef.beforeClosed());

        // if (!!event) {
        //     this.onEmailArchived(event);
        // }
    }

    public async openEmailArchiveForm() {
        const dialogRef: MatDialogRef<EmailArchiveDialogComponent, EmailArchivedEvent | undefined> = this.dialog.open(EmailArchiveDialogComponent, { data: { get: this.get, email: this.email }, width: 'min(75rem, 95vw)' });

        const event = await firstValueFrom(dialogRef.beforeClosed());

        if (!!event) {
            this.onEmailArchived(event);
        }
    }

    public async replyEmail(emailKeyno: string | number) {
        if ('' + emailKeyno > '0') {
            this.popup.openPopup(`emailcreate/emailview/email_keyno/${emailKeyno}`);
        } else {
            this.openEmailArchiveForm();
        }
    }

    public async replyAllEmail(emailKeyno: string | number) {
        if ('' + emailKeyno > '0') {
            // await Promise.all([this.remember.remember('w_emailadd_emails_to_add_to', this.email?.recipient_email.replaceAll(', ', '; ').replace(`${this.get?.current_mailbox}; `, '; ')), this.remember.remember('w_emailadd_emails_to_add_copy', this.email?.email_cc.replaceAll(', ', '; '))]);
            this.popup.openPopup(`emailcreate/emailview/email_keyno/${emailKeyno}?replyAll`);
        } else {
            this.openEmailArchiveForm();
        }
    }

    /**
     * Marks the currently selected email as spam to outlook (which outlook then will move to the spam folder) and removes the row from the grid.
     */
    public async markSelectedEmailAsSpam() {
        if (!!this.get && !!this.email) {
            try {
                await this.graphMail.markAsSpam(this.email.id, this.get.current_mailbox);
                this.removeRowAndSelectNext(this.email);
            } catch (error) {
                console.error(error);
                this.toast.addToast({ id: 'w_email.markasspam', title: 'An error occurred', message: error instanceof Error ? error.message : ' ', translateMessage: false, type: 'danger' });
            }
        }
    }

    /**
     * Deletes the currently selected email. \
     * If current folder is not `'deletedItems'` folder, the email is moved to `'deletedItems'` folder. \
     * If current folder is `'deletedItems'` folder, the email is permanently deleted.
     */
    public async deleteSelectedEmail() {
        if (!!this.get && !!this.email) {
            try {
                await this.deleteMessage(this.email);
                this.removeRowAndSelectNext(this.email);
            } catch (error) {
                console.error(error);
                this.toast.addToast({ id: 'w_email.deleteselected', title: 'An error occurred', message: error instanceof Error ? error.message : ' ', translateMessage: false, type: 'danger' });
            }
        }
    }

    /**
     * Deletes the selected emails from the grid. \
     * If current folder is not `'deletedItems'` folder, the emails are moved to `'deletedItems'` folder. \
     * If current folder is `'deletedItems'` folder, the emails are permanently deleted.
     */
    public async deleteSelectedRows() {
        this.grid.gridfunc?.getIsSelectedRows().forEach(async (row) => {
            await this.deleteMessage(row as EnrichedMessage);
            this.grid.gridfunc?.removeRow(row);
        });
    }

    /**
     * Creates vouchers from the selected rows.
     */
    private async createVouchersFromSelectedRows() {
        const selectedRows: EnrichedMessage[] = (this.grid.gridfunc?.getIsSelectedRows() ?? []) as EnrichedMessage[];

        if (selectedRows.length > 0) {
            try {
                this.grid.gridfunc?.gridProgress(true);
                delete this.email;

                for (let row of selectedRows as EnrichedMessage[]) {
                    await Promise.all([this.createVoucher(row), this.setEmailIsReadForGridRow(row)]);
                }

                await Promise.all(selectedRows.map((row) => this.updateEmailRow(row)));
            } finally {
                this.grid.gridfunc?.gridProgress(false);
            }
        }
    }

    private async setEmailIsReadForGridRow(row: GridRow) {
        try {
            const node = this.grid.gridfunc?.getRowNode(row);
            if (!!node) {
                row['read_ok'] = true;
                await this.setEmailIsRead(node, true);
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * Creates a voucher based on the given enriched email.
     *
     * @param email the email to create a voucher based on.
     */
    private async createVoucher(email: EnrichedMessage) {
        if (!!this.get) {
            const attachments = await this.getEmailAttachments(email);
            const htmlContent = this.base64.urlencode(this.replaceInlineAttachmentsWithContentBytes(this.base64.urldecode(email.email_body_html), attachments, false));

            const voucher = this.emailService.serializeEmail(email);
            voucher.attachments = attachments.filter((attachment) => attachment.isInline === false);
            voucher.email_body_html = htmlContent;
            voucher.create_voucher = '1';

            const { errorcode, errormessage } = await this.emailService.saveEmailItem(voucher as EnrichedMessage);

            if (errorcode !== '0') {
                this.modal.show({ title: 'error', translateTitle: true, message: errormessage });
            } else if (this.canMoveToArchiveFolder()) {
                await this.graphMail.moveMessageToFolder(email.id, this.get.auto_archieve_folder_id, this.get.current_mailbox);
                this.grid.gridfunc?.removeRow(email);
            }
        }
    }

    /**
     * Removes data based upon the current mailbox.
     */
    private reset = () => {
        delete this.deletedItemsFolderId;
        delete this.email;
        this.grid.gridfunc?.gridProgress(true);
        this.grid.gridfunc?.setDataSource([]);
        // this.account = null;
    };

    /**
     * Removes the given row and selects the next available row.
     *
     * @param row the row to remove.
     */
    private async removeRowAndSelectNext(row: GridRow) {
        const emailToMove = Object.assign({}, row);

        delete this.email;

        const rowIndex = this.grid.gridfunc?.getRowIndex(emailToMove);
        await this.grid.gridfunc?.removeRow(emailToMove);

        setTimeout(() => this.selectRowAtRowIndex(rowIndex ?? 0));
    }

    /**
     * Selectes the row at the given index, or the closest to it if index is out of bounds.
     *
     * @param rowIndex the index of the row to select.
     */
    private selectRowAtRowIndex(rowIndex: number) {
        if (!!this.grid?.gridfunc) {
            const visibleRowCount = this.grid.gridfunc.getGridApi().getDisplayedRowCount();

            if (rowIndex >= visibleRowCount - 1) {
                rowIndex = visibleRowCount - 1;
            }

            if (rowIndex >= 0) {
                this.grid.gridfunc.selectRow(rowIndex);
            } else {
                delete this.email;
            }
        }
    }

    /**
     * Handles change of current mailbox.
     *
     * @param _ the new current mailbox.
     */
    public async onMailboxChanged() {
        if (!!this.get) {
            this.reset();

            await this.remember.remember('w_email.current_mailbox.' + this.get.current_account_email, this.get.current_mailbox);

            await Promise.all([/*this.loadMailFolders(),*/ this.loadPageGet()]);

            if (this.isInvalidSelectedFolder()) await this.setDefaultMailFolder();

            if (this.get.gb_email_main === 'LAST_ARCHIVED') {
                this.readLastArchivedGrid();
            } else {
                this.reloadGridData();
            }
        }
    }

    /**
     * Handles change in the selected mail folder.
     *
     * @param _ the new selection.
     */
    public onMailFolderChanged(items: MailFolder[]) {
        console.log('onMailFolderChanges');
        console.log(items);
        if (!this.get) return;

        delete this.email;
        this.get.selected_folder_name = items[0]?.displayName ?? '';
        this.remember.remember('w_email.selected_folder_name.' + this.get.current_account_email + '.' + this.get.current_mailbox, this.get.selected_folder_name);
        this.reloadGridData();

        if (this.get.gb_email_main === 'LAST_ARCHIVED') {
            this.get.gb_email_main = 'FOCUSED';
        }
    }

    /**
     * Handles change in archive folder.
     *
     * @param item the archive folder selected.
     */
    public onArchiveFolderSelected(item?: MailFolderSearchItem) {
        if (!!this.get) {
            this.get.auto_archieve_folder_id = item?.item_id ?? '';
            this.get.auto_archieve_folder_name = item?.item_name ?? '';
            this.remember.remember(`w_email.auto_archieve_folder_id.${this.get.current_account_email}.${this.get.current_mailbox}`, this.get.auto_archieve_folder_id);
            this.remember.remember(`w_email.auto_archieve_folder_name.${this.get.current_account_email}.${this.get.current_mailbox}`, this.get.auto_archieve_folder_name);
        }
    }

    public searchArchiveMailFolders = async (): Promise<MailFolderSearchItem[]> => {
        const searchtext = this.get?.auto_archieve_folder_name?.trim() ?? '';

        if (!this.get) {
            return [];
        }

        return this.graphMail.searchMailFolders({ userId: this.get.current_mailbox, searchtext });
    };

    /**
     * Updates all items to the newly selected business co.
     *
     * @param event the id of the new business co selected.
     */
    public onBusinessCoChanged(event: string) {
        if (!!this.email) this.email.businessco_no = event;

        this.grid.gridfunc?.setDataSource(this.grid.gridfunc?.getAllRows().map((row) => ({ ...row, businessco_no: event })));
    }

    /**
     * Handles changes is folder search settings.
     */
    public onSearchFolderChanged() {
        if (!!this.get && !!this.get.email_search_global?.trim()) {
            this.reloadGridData();
        }
    }

    /**
     * Handles search for email.
     */
    public onEmailSearchChanged() {
        if (!!this.get && !this.get.email_search_global?.trim()) {
            this.get.local_search = '0';
        }

        this.reloadGridData();
    }

    public onTabChanged() {
        if (!this.get) return;

        if (this.get.gb_email_main === 'LAST_ARCHIVED') {
            delete this.email;
            this.readLastArchivedGrid();
        } else {
            this.reloadGridData();
        }
    }

    /**
     * Request a new load of grid data.
     */
    public async reloadGridData() {
        console.log('reloadGridData');

        this.reloadData$.next();
    }

    /**
     * Retrieves the messages from microsoft based on the current settings, mapped to a TouchTime friendly format.
     *
     * @returns messages from microsoft based on the current settings, mapped to a TouchTime friendly format.
     */
    public async getMessageEmails() {
        console.log('getMessageEmails');

        if (!!this.get) {
            if (this.get.gb_email_main === 'LAST_ARCHIVED') return [];
            return this.emailService.getMessageEmails({ folderId: this.get.local_search == '0' && !!this.get.email_search_global ? undefined : this.get.selected_folder_id, limit: +this.get.retrieve_max_nbr_of_emails, userId: this.get.current_mailbox, search: this.get.email_search_global, filter: this.getInferenceFilter() });
        }

        console.error('Get must be loaded before retrieving messages from mgt');
        return [];
    }

    /**
     * Retrieves the current inference filter as a microsoft graph api friendly filter string used for request.
     *
     * @returns filter query string for inference to use for microsoft graph request.
     */
    private getInferenceFilter() {
        if (!this.get) return '';

        if (this.get.gb_email_main === 'FOCUSED') {
            return `inferenceClassification eq 'focused'`;
        }

        if (this.get.gb_email_main === 'OTHER') {
            return `inferenceClassification eq 'other'`;
        }

        return '';
    }

    /**
     * Enriches the given messages and returns the merged object of the enriched messages with the messages from outlook.
     *
     * @param messages the messages to enrich.
     * @returns promise resolving in the enriched messages.
     */
    public async getEnrichedMessages(messages: MessageEmail[]) {
        if (!!this.get) {
            return this.emailService.getEnrichedMessages({ businessco_no: this.get!.businessco_no, current_account_email: this.get!.current_account_email, emails: messages });
        }

        console.error('Get must be loaded before retrieving enriched messaged');
        return [];
    }

    /**
     * Finds the id of the inbox folder of the user and sets it as the selected folder id.
     */
    private async setDefaultMailFolder() {
        if (!!this.get) {
            this.get.selected_folder_id = (await this.graphMail.getMailFolder('inbox', this.get.current_mailbox)).id ?? '';
        } else {
            throw Error('Get must be loaded before default folder can be found');
        }
    }

    /**
     * Checkes whether the current settings can allow for archiving to the auto-archive folder
     *
     * @returns `true` if moving to archive folder can work with current settings, `false` if not.
     */
    private canMoveToArchiveFolder() {
        return !!this.get && this.get.auto_archieve_folder_id !== '0' && !!this.get.auto_archieve_folder_id && this.get.selected_folder_id !== this.get.auto_archieve_folder_id;
    }

    /**
     * Checks if the currently selected folder is missing.
     *
     * @returns `true` if the currently selected folder id is missing, `false` otherwise.
     */
    private isInvalidSelectedFolder() {
        return !!this.get && !this.get.selected_folder_id;
    }

    /**
     * Sets the initial data retrieved upon page load to the grid.
     */
    public async setInitialDataToGrid() {
        const messages = await this.retrievingInitialEmailsPromise;
        this.grid.gridfunc?.setDataSource(messages);
        const enrichedMessages = await this.retrievingInitialEnrichedEmailsPromise;
        this.grid.gridfunc?.setDataSource(enrichedMessages);
    }

    /**
     * Load companies list.
     */
    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    /**
     * Retrieves initial page load.
     */
    private async loadPageGet() {
        this.get = await this.emailService.getEmailPageLoad();
    }

    /**
     * Loads shared mailboxes based on the current account email.
     */
    private async loadSharedMailboxes() {
        if (!!this.get) {
            this.sharedMailboxes = await this.emailService.getSharedMailboxes(this.get.current_account_email);
        }
    }

    /**
     * Remembers the current account email which is logged on.
     *
     * @returns a promsie resolving once the email as been remembered.
     */
    private rememberCurrentAccount() {
        return this.remember.remember('w_email.current_account_email', this.get?.current_account_email ?? '');
    }

    /**
     * Flag for when login has been initiated.
     */
    private loginInitiated = false;

    /**
     * Handles login initiated event.
     */
    private onLoginInitiated = () => {
        this.loginInitiated = true;
    };

    /**
     * Handles user logged in event.
     */
    private onUserLoggedIn = () => {
        console.log('onUserLoggedIn');
        if (this.loginInitiated) {
            this.userLoggedIn$.next();
        }
    };

    public account?: AccountInfo | null;

    /**
     * Retrieves data once the user has logged in and the account has been retrieved.
     *
     * @param account the account for the user logged in.
     */
    private onUserLoggedInAndAccountRetrieved = async (account: AccountInfo | null) => {
        console.log('onUserLoggedInAndAccountRetrieved');

        this.account = account;

        if (!!this.get && !!account) {
            this.get.current_account_email = account.username;
            await this.rememberCurrentAccount();
            await this.loadPageGet();
            await this.loadSharedMailboxes();
            // this.loadMailFolders();
            this.forceMailFoldersReload();
            this.reloadGridData();
        }
    };

    private forceMailFoldersReload() {
        if (!this.get) return;
        const mailbox = this.get.current_mailbox;
        this.get.current_mailbox = '';
        setTimeout(() => (this.get!.current_mailbox = mailbox));
    }

    /**
     * Subscription for logout events.
     */
    private logoutSubscription?: Subscription;

    /**
     * Handles logout event.
     */
    private onUserLogoutInitiated = () => {
        if (!!this.logoutSubscription) this.logoutSubscription.unsubscribe();
        // @ts-ignore - flyout gets stuck open but this function is protected.
        this.login?.nativeElement.hideFlyout();

        this.logoutSubscription = this.graphAuth.stateChange$.subscribe({
            next: () => {
                this.getMicrosoftAccount(4).subscribe({
                    error: () => {
                        this.reset();
                        this.account = null;
                    },
                    complete: () => {
                        this.logoutSubscription?.unsubscribe();
                        this.account = null;
                    },
                });
            },
        });
    };

    /**
     * Handles failed account retrieval.
     *
     * @param error the error given by microsoft graph api.
     */
    private onGetMicrosoftAccountFailed = (error: unknown) => {
        console.error(error);
        this.account = null;
        this.toast.addToast({ id: 'w_email.accountnotfound', title: 'could_not_connect_to_account', translateTitle: true, message: error instanceof Error ? error.message : ' ', translateMessage: false, type: 'danger' });
    };

    /**
     * Attempts to retrieve the microsoft account.
     *
     * @param attempts number of attempts before failing.
     * @param intervalBetweenAttempts time in ms to wait between each attempt.
     * @returns observable which either returns the account found, or fails and returns null.
     */
    private getMicrosoftAccount(attempts: number = 6, intervalBetweenAttempts: number = 1000) {
        console.log('getMicrosoftAccount');
        return interval(intervalBetweenAttempts).pipe(
            takeUntil(timer(attempts * intervalBetweenAttempts)),
            switchMap(() =>
                from(this.graphAuth.getAccount()).pipe(
                    map((account) => account),
                    catchError(() => of(null)),
                ),
            ),
            filter((result) => !!result),
            first(),
        );
    }

    /**
     * The id of the deleted items mailfolder.
     */
    private deletedItemsFolderId?: string;

    /**
     * If current mailfolder is not the `'deletedItems'` folder then the given mail is moved to the `'deletedItems'` folder. \
     * If current mailfolder is the `'deletedItems'` folder then the given mail is permanently deleted.
     *
     * @param message
     */
    private async deleteMessage(message: EnrichedMessage) {
        if (!!this.get) {
            if (!this.deletedItemsFolderId) {
                this.deletedItemsFolderId = (await this.graphMail.getMailFolder('deleteditems'))?.id;
            }

            if (!!this.deletedItemsFolderId && this.deletedItemsFolderId === this.get.selected_folder_id) {
                await this.graphMail.deleteMessage(message.id, this.get.current_mailbox);
            } else {
                await this.graphMail.moveMessageToDeletedItems(message.id, this.get.current_mailbox);
            }
        }
    }

    ngOnDestroy(): void {
        this.login?.nativeElement.removeEventListener('click', this.onLoginInitiated);
        this.login?.nativeElement.removeEventListener('loginCompleted', this.onUserLoggedIn);
        this.login?.nativeElement.removeEventListener('logoutInitiated', this.onUserLogoutInitiated);
        this.logoutSubscription?.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.login?.nativeElement.addEventListener('click', this.onLoginInitiated);
        this.login?.nativeElement.addEventListener('loginCompleted', this.onUserLoggedIn);
        this.login?.nativeElement.addEventListener('logoutInitiated', this.onUserLogoutInitiated);
    }
}
